// Operator vocabulary — FD-ADR-031 §3.
//
// The console shows **Hybrid** and **Cloud**. The wire, the tables, the events,
// the flight plans and the device contract all keep **D2C** and **T2T**.
//
// THIS MODULE IS THE ONLY PLACE THE TWO MEET. That is the whole design: a
// vocabulary change that reached the wire would be a cross-repo contract change
// touching flight-agent and ATC, for a labelling preference. Map at the render
// boundary, reverse on submit, and never anywhere else.
//
// The risk this creates is a HALF-APPLIED rename — the console saying "Hybrid"
// in one place and `D2C` in another, or a helper that maps one way and not the
// other. FD-270 is the precedent: FD-236 inverted the Autopilot tag precedence
// and FD-235 corrected three documents, yet FOUR in-code statements of the old
// rule survived, one of them a field hint on the create form. The check for that
// class of defect is a grep across every layer, not a reread of the decision —
// which is why both directions live here, next to each other, rather than as an
// inline ternary wherever a label is needed.

/** The wire value. Never renamed, never localised, never inferred from a label. */
export type MigrationTypeWire = 'D2C' | 'T2T';

/** What an operator reads. Never sent to a server. */
export type MigrationTypeLabel = 'Hybrid' | 'Cloud';

/**
 * Wire → label.
 *
 * An unrecognised value is returned VERBATIM rather than mapped to a default.
 * A migration whose type the console does not understand should look unfamiliar;
 * silently labelling it "Hybrid" would hide a real data problem behind a
 * plausible word.
 */
export function migrationTypeLabel(wire: string | null | undefined): string {
  switch ((wire ?? '').toUpperCase()) {
    case 'D2C':
      return 'Hybrid';
    case 'T2T':
      return 'Cloud';
    default:
      return wire ?? '';
  }
}

/** Label → wire. Used on submit; returns null for anything unrecognised. */
export function migrationTypeWire(label: string | null | undefined): MigrationTypeWire | null {
  switch ((label ?? '').toLowerCase()) {
    case 'hybrid':
      return 'D2C';
    case 'cloud':
      return 'T2T';
    default:
      return null;
  }
}

/**
 * The one-line explanation of what each type actually does to a device, for the
 * onboarding wizard and any surface that has room for it.
 *
 * Written in terms of what happens to the DEVICE, not in terms of tenancy
 * topology — the operator choosing between these is answering "what are my
 * machines joined to today", not "which acronym applies".
 */
export function migrationTypeDescription(wire: string | null | undefined): string {
  switch ((wire ?? '').toUpperCase()) {
    case 'D2C':
      return 'Devices leave the on-premises domain and join Microsoft Entra ID.';
    case 'T2T':
      return 'Devices move from one Microsoft Entra tenant to another.';
    default:
      return '';
  }
}
